import React, { useEffect, useState } from 'react';
import DefaultReactTable from '../../utils/react-table';
import { connect } from 'react-redux';
import { isEmpty, difference, orderBy } from 'lodash';

import { selectors as usersSelectors } from '../../state/modules/users';
import { selectors as groupsSelectors } from '../../state/modules/groups';
import { selectors as skillsSelectors } from '../../state/modules/skills';
import { selectors as membershipsSelectors } from '../../state/modules/memberships';
import SkillIconList from '../general/SkillIconList';
import DashboardAPIManager from '../dashboards/DashboardAPIManager';
import Loader from '../general/Loader';
import GroupName from '../general/GroupName';

const createColumns = (usersById, skillsById, groupsById, allSelected, selectAll, isSelected, toggleMembership) => [
  {
    Header: <input type="checkbox" checked={allSelected()} onChange={selectAll} />,
    id: 'toggle',
    maxWidth: 30,
    resizable: false,
    sortable: false,
    Cell: row => (
      <input
        type="checkbox"
        checked={isSelected(row.original.id)}
        onChange={() => toggleMembership(row.original.id)}
        value={row.original.id}
      />
    ),
    filterable: false
  },
  {
    Header: `${I18n.t('name')}`,
    accessor: d => {
      const user = usersById[d.user_id];
      return `${user?.first_name} ${user?.last_name}`;
    },
    id: 'name',
    Cell: row => {
      const user = usersById[row.original.user_id];
      return `${user?.first_name} ${user?.last_name}`;
    }
  },
  {
    Header: `${I18n.t('activerecord.models.group')}`,
    accessor: d => {
      const group = groupsById[d.group_id];
      return group?.name;
    },
    id: 'group',
    Cell: row => <GroupName group={groupsById[row.original.group_id]} fullName={true} />
  },
  {
    Header: `${I18n.t('activerecord.models.skill')}`,
    accessor: d => {
      return d.skills
        .map(s => {
          const skill = skillsById[s];
          return skill?.name;
        })
        .join(',');
    },
    id: 'skills',
    Cell: row => {
      const allSkillIds = row.original.skills;
      return <SkillIconList skills={allSkillIds.map(s => skillsById[s])} showScheduleIconsOnly={true} />;
    }
  }
];

const MembershipSelectTable = props => {
  useEffect(() => {
    // The holdInitialLoad is a flag that lets the component know
    // whether the data must be loaded immediately or hold until
    // an event is emitted. This prevents loading data that may
    // impact performance. For example, when creating an incident
    if (props.holdInitialLoad) {
      document.addEventListener('loadMemberships', loadData);
    } else {
      loadData();
    }

    setIdsSelected(props.selectedMembershipIds);
  }, []);

  const loadData = () => {
    const apiManager = new DashboardAPIManager();
    const { groupScopeId } = props;
    apiManager.setDefaultGroupId(groupScopeId);
    apiManager.fetchGroups({ params: { presentation_type: 'basic', group_type: 'station' } });
    apiManager.fetchUsers({ params: { presentation_type: 'basic' } });
    apiManager.fetchSkills({ params: { presentation_type: 'basic' } });
    apiManager.fetchMemberships({ params: { group_type: 'station' } });
  };

  const [idsSelected, setIdsSelected] = useState([]);
  const [filteredMemberships, setFilteredMemberships] = useState([]);

  const isSelected = membershipId => idsSelected.includes(membershipId);

  const toggleMembership = membershipId => {
    let newIdsSelected = [];
    newIdsSelected = isSelected(membershipId)
      ? idsSelected.filter(id => id !== membershipId)
      : [...idsSelected, membershipId];

    setIdsSelected(newIdsSelected);
  };

  const allSelected = () => {
    const displayed = filteredMemberships.map(m => m?.id);
    const selected = idsSelected;

    return isEmpty(difference(displayed, selected));
  };

  // Selects all memberships displayed in the table, if all users are selected
  // then clear the selection
  const selectAll = () => {
    if (allSelected()) {
      clearAll();
    } else {
      const membershipIds = filteredMemberships.map(m => m?.id);
      setIdsSelected(membershipIds);
    }
  };

  // Clears all selected memberships
  const clearAll = () => {
    setIdsSelected([]);
  };

  if (!props.usersLoaded || !props.groupsLoaded || !props.skillsLoaded || !props.membershipsLoaded) return <Loader />;

  const applicableGroupMemberships = orderBy(
    props.memberships.filter(m => {
      // return active memberships for stations
      const group = props.groupsById[m.group_id];
      return m.status !== 'inactive' && group?.type === 'station';
    }),
    ['group_id', 'display_order'],
    ['asc', 'asc']
  );

  return (
    <React.Fragment>
      {/* Render hidden inputs independent from pagination */}
      {idsSelected.map(id => (
        <input type="hidden" name={props.inputName} key={id} value={id} />
      ))}
      <DefaultReactTable
        filterable
        columns={createColumns(
          props.usersById,
          props.skillsById,
          props.groupsById,
          allSelected,
          selectAll,
          isSelected,
          toggleMembership
        )}
        data={applicableGroupMemberships}
        onFetchDataHandler={state => {
          setFilteredMemberships(state.sortedData.map(row => row['_original']));
        }}
        getTrProps={(_, row) => {
          const selected = idsSelected.includes(row?.original?.id);
          return {
            onClick: () => toggleMembership(row.original.id),
            style: {
              background: selected ? 'var(--bs-primary)' : 'var(--bs-body-bg)',
              color: selected ? 'var(--bs-btn-active-color)' : 'var(--bs-body-color)'
            }
          };
        }}
        defaultSorted={[{ id: 'group', desc: true }]}
      />
    </React.Fragment>
  );
};

const mapStateToProps = state => ({
  usersLoaded: usersSelectors.initialLoadComplete(state),
  groupsLoaded: groupsSelectors.initialLoadComplete(state),
  skillsLoaded: skillsSelectors.initialLoadComplete(state),
  membershipsLoaded: membershipsSelectors.initialLoadComplete(state),
  usersById: usersSelectors.byId(state),
  memberships: membershipsSelectors.values(state),
  skillsById: skillsSelectors.byId(state),
  groupsById: groupsSelectors.byId(state)
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(MembershipSelectTable);
